import { useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { ExportMyProfileDocument } from "@/graphql/generated/operations";
import { downloadBlob } from "@/lib/file-download";
import { userErrorMessage } from "@/lib/user-error";

interface ProfileYamlExportDialogProps {
  onClose: () => void;
  onError?: (message: string) => void;
}

export function ProfileYamlExportDialog({ onClose, onError }: ProfileYamlExportDialogProps) {
  const apollo = useApolloClient();
  const intl = useIntl();
  const [profile, setProfile] = useState(true);
  const [documents, setDocuments] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasSelection = profile || documents;

  async function download() {
    if (busy || !hasSelection) return;
    setBusy(true);
    setErrorMessage(null);
    try {
      const result = await apollo.query({
        query: ExportMyProfileDocument,
        variables: { input: { profile, documents } },
        fetchPolicy: "no-cache",
      });
      const yaml = result.data?.exportMyProfile;
      if (yaml == null) throw new Error("empty YAML export response");

      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      downloadBlob(
        new Blob([yaml], { type: "application/yaml;charset=utf-8" }),
        `smycles-yaml-export-${stamp}.yaml`,
      );
      onClose();
    } catch (error) {
      const message = userErrorMessage(intl, error);
      setErrorMessage(message);
      onError?.(message);
    } finally {
      setBusy(false);
    }
  }

  const option = (
    checked: boolean,
    setChecked: (checked: boolean) => void,
    labelId: string,
    label: string,
    descriptionId: string,
    description: string,
  ) => (
    <label className="box-flat flex cursor-pointer items-start gap-3 bg-page px-4 py-3 hover:bg-ink/[0.06]">
      <input
        type="checkbox"
        checked={checked}
        disabled={busy}
        onChange={(event) => setChecked(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-ink"
      />
      <span>
        <span className="block text-[12px] font-semibold tracking-wide text-ink">
          <FormattedMessage id={labelId} defaultMessage={label} />
        </span>
        <span className="field-meta mt-1 block leading-snug text-ink/55">
          <FormattedMessage id={descriptionId} defaultMessage={description} />
        </span>
      </span>
    </label>
  );

  return (
    <Modal
      open
      onClose={busy ? () => undefined : onClose}
      title={<FormattedMessage id="service.yamlExport.title" defaultMessage="YAML export" />}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" onClick={() => void download()} disabled={busy || !hasSelection}>
            {busy ? (
              <FormattedMessage id="service.yamlExport.exporting" defaultMessage="Exporting…" />
            ) : (
              <FormattedMessage id="service.yamlExport.download" defaultMessage="Download YAML" />
            )}
          </Button>
        </>
      }
    >
      <p className="field-meta mb-4 leading-snug text-ink/80">
        <FormattedMessage
          id="service.yamlExport.intro"
          defaultMessage="Choose which areas to include in the export. You can import the downloaded file again with the YAML importer."
        />
      </p>
      <div className="space-y-2">
        {option(
          profile,
          setProfile,
          "service.yamlExport.profile",
          "Profile",
          "service.yamlExport.profileDesc",
          "Personal details, portraits, experience, education, expertise, languages and certificates",
        )}
        {option(
          documents,
          setDocuments,
          "service.yamlExport.documents",
          "Documents",
          "service.yamlExport.documentsDesc",
          "Document folders and files, including metadata, tags and file content. Without Profile, the person must already exist when importing.",
        )}
      </div>
      {!hasSelection ? (
        <p role="alert" className="field-meta mt-3 text-danger">
          <FormattedMessage
            id="service.yamlExport.selectionRequired"
            defaultMessage="Select at least one area."
          />
        </p>
      ) : null}
      {errorMessage ? (
        <p role="alert" className="field-meta mt-3 text-danger">
          {errorMessage}
        </p>
      ) : null}
    </Modal>
  );
}
